import { useContext } from 'react';

import { mapUtil, MapContextType } from '@stormwater-software/common';

import { Context as InstanceDataContext } from '../Positionable/InstanceDataContext';
import { Context as DataSourceContext } from '../DataSourceContext';
import { MapEditorRouteContext } from '../../CustomRoutes/MapEditor';
import { setFeatureData } from '../Interaction/Draw/Draw';
import { getTargetKey } from '../Positionable/DataSource';
import { getUtil } from '../../../utils/positionable';
import { isLatLngInMap, latLngToPixel } from '../../../utils/geo';

const ASSET_TYPE = 'asset_type';

// Already on this map, saved or pending. `countInstances` is dead — see README #1.
export function isAssetPlaced(asset, savedRows, pendingEntries, args) {
  const id = Number(asset?.id);

  const saved = (savedRows || []).some(
    (row) => Number(row.positionable_id) === id && !row.marked_deleted,
  );

  if (saved) {
    return true;
  }

  return (pendingEntries || []).some(
    (entry) =>
      entry.positionableType === ASSET_TYPE &&
      entry.targetType === args.targetType &&
      entry.targetId === args.targetId &&
      entry.startInspectionId === args.startInspectionId &&
      Number(entry.sourceModelId) === id,
  );
}

// Which assets can be auto-placed. Pure, so it is testable without the editor.
export function partitionAssetsForAutoPlace(assets, projectUpload, isPlaced) {
  const result = { placeable: [], alreadyPlaced: [], notLocatable: [] };

  if (!Array.isArray(assets) || !projectUpload) {
    return result;
  }

  assets.filter((asset) => !asset?.archived_at).forEach((asset) => {
    const lat = Number(asset?.latitude);
    const lng = Number(asset?.longitude);

    // DECIMAL columns can arrive as strings, so coerce before checking.
    const hasCoordinates =
      asset?.latitude != null &&
      asset?.longitude != null &&
      asset.latitude !== '' &&
      asset.longitude !== '' &&
      Number.isFinite(lat) &&
      Number.isFinite(lng);

    if (!hasCoordinates || !isLatLngInMap(lat, lng, projectUpload)) {
      result.notLocatable.push(asset);
    } else if (isPlaced(asset)) {
      result.alreadyPlaced.push(asset);
    } else {
      result.placeable.push(asset);
    }
  });

  return result;
}

// Bulk auto-place in-bounds assets as pending updates; the toolbar's Save keeps them.
function useAssetAutoPlace({ enabled = true } = {}) {
  const mapContext = useContext(MapContextType);
  const dataContext = useContext(InstanceDataContext);
  const dataSource = useContext(DataSourceContext);
  const routeContext = useContext(MapEditorRouteContext);

  const projectUpload = routeContext?.projectUpload;
  const gisCalibrated = !!routeContext?.gisCalibrated;
  const olMap = mapContext?.map ?? null;

  // The map image's pixel dimensions are the OL projection extent.
  const getImageSize = () => {
    const extent = olMap?.getView()?.getProjection()?.getExtent();

    if (!extent) return null;

    const [, , imgW, imgH] = extent;

    return imgW && imgH ? { imgW, imgH } : null;
  };

  // Map pixel for an asset's lat/long on a calibrated map; null when it can't go there.
  const pixelFor = (asset) => {
    const size = getImageSize();
    const lat = Number(asset?.latitude);
    const lng = Number(asset?.longitude);

    if (!gisCalibrated || !projectUpload || !size) return null;
    if (!isLatLngInMap(lat, lng, projectUpload)) return null;

    return latLngToPixel(lat, lng, size.imgW, size.imgH, projectUpload);
  };

  const typeArguments = () =>
    dataSource?.getDataTypeArguments
      ? dataSource.getDataTypeArguments(ASSET_TYPE)
      : null;

  // Without this, a second click would double every marker.
  const isPlaced = (asset, args) => {
    const savedRows = dataSource.get(
      getTargetKey(
        ASSET_TYPE,
        args.targetType,
        args.targetId,
        args.startInspectionId,
      ),
    );
    const pendingEntries = Object.values(
      dataContext.pendingUpdates?.instance || {},
    );

    return isAssetPlaced(asset, savedRows, pendingEntries, args);
  };

  // Split the list so the caller can both disable the button and explain why.
  const partition = () => {
    const args = typeArguments();
    const assets = dataSource?.get ? dataSource.get(ASSET_TYPE) : null;

    if (!args) {
      return { placeable: [], alreadyPlaced: [], notLocatable: [] };
    }

    return partitionAssetsForAutoPlace(assets, projectUpload, (asset) =>
      isPlaced(asset, args),
    );
  };

  // Place every placeable asset at its own coordinates.
  const autoPlaceAll = () => {
    const { placeable, alreadyPlaced, notLocatable } = partition();
    const size = getImageSize();
    const args = typeArguments();
    const util = getUtil(ASSET_TYPE);

    const place = (asset) => {
      const pixel = pixelFor(asset);

      if (!pixel) return false;

      // Marker styles are per-asset, so layers are resolved per asset.
      const layer = mapContext.getLayer(ASSET_TYPE, asset);
      const source = layer.getSource();
      const feature = mapUtil.getPointFeature(pixel[0], pixel[1]);

      setFeatureData(feature, source, asset, util);
      source.addFeature(feature);

      dataContext.setPendingUpdate(
        feature,
        ASSET_TYPE,
        args.targetType,
        args.targetId,
        args.startInspectionId,
        args.endInspectionId,
      );

      return true;
    };

    const placed =
      size && args ? placeable.filter(place).length : 0;

    return {
      placed,
      alreadyPlaced: alreadyPlaced.length,
      notLocatable: notLocatable.length,
    };
  };

  // Placement is derived from lat/long, so the button needs a calibrated map.
  const available = enabled && gisCalibrated && !!projectUpload && !!olMap;

  // Duplicate prevention is not GIS-specific, so this ignores `gisCalibrated`.
  const isUsedHere = (asset) => {
    const args = typeArguments();

    return enabled && args ? isPlaced(asset, args) : false;
  };

  // Called from every sidebar section for a fixed hook order; only Assets works.
  const counts = available
    ? partition()
    : { placeable: [], alreadyPlaced: [], notLocatable: [] };

  return {
    // Only meaningful on a geo-calibrated map with a live OL map.
    available,
    isUsedHere,
    placeableCount: counts.placeable.length,
    alreadyPlacedCount: counts.alreadyPlaced.length,
    notLocatableCount: counts.notLocatable.length,
    autoPlaceAll,
    pixelFor,
  };
}

export default useAssetAutoPlace;
